<template>
  <div v-if="availableTagTypes.length > 0" class="tag-selector-container">
    <a-form-item
      v-for="tagType in availableTagTypes"
      :key="tagType.id"
      :label="tagType.type_name"
      :required="isRequired(tagType.type_code)"
      :label-col="{ span: 4 }"
      :wrapper-col="{ span: 20 }"
    >
      <template v-if="tagType.is_cascade === 1">
        <!-- 联动标签选择器 -->
        <CascadeTagSelector
          :tag-type="tagType"
          :value="getTagValue(tagType.type_code)"
          :required="isRequired(tagType.type_code)"
          @change="(value) => handleTagChange(tagType, value)"
        />
      </template>
      <template v-else>
        <!-- 独立标签选择器 -->
        <!-- 单选框方式：故障现象、系统电压、发动机信息 -->
        <template v-if="isSingleSelect(tagType.type_code)">
          <a-radio-group
            :value="getSingleSelectValue(tagType.type_code)"
            @change="(e) => handleRadioChange(tagType, e)"
          >
            <a-radio
              v-for="tag in independentTagsMap[tagType.id] || []"
              :key="tag.id"
              :value="Number(tag.id)"
              :disabled="loadingTags[tagType.id]"
              @click="() => handleRadioClick(tagType, Number(tag.id))"
            >
              {{ tag.tag_name }}
            </a-radio>
          </a-radio-group>
          <a-button 
            v-if="getSingleSelectValue(tagType.type_code) !== undefined"
            type="link" 
            size="small" 
            style="margin-left: 8px; padding: 0"
            @click="() => handleClearSingleSelect(tagType)"
          >
            清除
          </a-button>
        </template>
        <!-- 多选标签方式：其他独立标签 -->
        <div v-else style="display: flex; flex-wrap: wrap; gap: 8px">
          <a-tag
            v-for="tag in independentTagsMap[tagType.id] || []"
            :key="tag.id"
            :color="isTagSelected(tagType.type_code, tag.id) ? 'blue' : 'default'"
            style="cursor: pointer; padding: 4px 12px; margin: 0"
            @click="handleTagToggle(tagType, tag.id)"
          >
            {{ tag.tag_name }}
          </a-tag>
          <span v-if="loadingTags[tagType.id]" style="color: #999; padding: 4px 12px">加载中...</span>
          <span v-if="!loadingTags[tagType.id] && (!independentTagsMap[tagType.id] || independentTagsMap[tagType.id].length === 0)" style="color: #999; padding: 4px 12px">暂无标签</span>
        </div>
      </template>
    </a-form-item>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { getTagTypes, getTagsByType, getCascadeTags } from '../api/tagType';
import CascadeTagSelector from './CascadeTagSelector.vue';

export default {
  name: 'TagSelector',
  components: {
    CascadeTagSelector
  },
  props: {
    knowledgeTypeCode: {
      type: String,
      required: false,
      default: ''
    },
    value: {
      type: Array,
      default: () => []
    }
  },
  emits: ['change'],
  setup(props, { emit }) {
    const tagTypes = ref([]);
    const independentTagsMap = reactive({});
    const cascadeTagsMap = ref({});
    const selectedTags = reactive({});
    const loadingTags = reactive({});

    // 知识类型与标签的映射关系
    // 注意：调用步骤通过 StepSelector 组件选择，不是标签
    // 标签类型代码使用数据库中的实际值（大写）
    const KNOWLEDGE_TYPE_TAG_MAP = {
      prompt: {
        // 提示词：故障现象（必选）、控制单元信息、专项分析（可选）
        required: ['FAULT_PHENOMENON'],
        optional: ['CONTROL_UNIT', 'Special_Analysis']
      },
      prompt_template: {
        // 提示词结构：专项分析（可选）
        required: [],
        optional: ['Special_Analysis']
      },
      dataflow: {
        // 数据流：故障现象（必选）、控制单元信息、专项分析（可选）
        required: ['FAULT_PHENOMENON'],
        optional: ['CONTROL_UNIT', 'Special_Analysis']
      },
      pin_info: {
        // 针脚信息：控制单元信息（必选）、系统电压（必选）
        required: ['CONTROL_UNIT', 'SYSTEM_VOLTAGE'],
        optional: []
      },
      case: {
        // 典型案例：整车信息、发动机信息、控制单元信息、系统、部件、专项分析（全部可选）
        required: [],
        optional: ['VEHICLE_INFO', 'ENGINE_INFO', 'CONTROL_UNIT', 'SUBSYSTEM_INFO', 'COMPONENT_INFO', 'Special_Analysis']
      },
      principle: {
        // 系统原理：整车信息、发动机信息、控制单元信息、系统、部件、专项分析（全部可选）
        required: [],
        optional: ['VEHICLE_INFO', 'ENGINE_INFO', 'CONTROL_UNIT', 'SUBSYSTEM_INFO', 'COMPONENT_INFO', 'Special_Analysis']
      },
      standard_datastream: {
        // 参考数据流：整车信息、发动机信息、控制单元信息、系统、部件、专项分析（全部可选）
        required: [],
        optional: ['VEHICLE_INFO', 'ENGINE_INFO', 'CONTROL_UNIT', 'SUBSYSTEM_INFO', 'COMPONENT_INFO', 'Special_Analysis']
      },
      fc_set: {
        // 故障码集：控制单元信息（可选）
        required: [],
        optional: ['CONTROL_UNIT']
      },
      ecu_pack: {
        // ECU素材：控制单元信息（可选）
        required: [],
        optional: ['CONTROL_UNIT']
      },
      sensor_pack: {
        // 部件素材：部件（可选）
        required: [],
        optional: ['COMPONENT_INFO']
      },
      synonymDataStreams: {
        // 同义数据流：专项分析（可选）
        required: [],
        optional: ['Special_Analysis']
      },
      other: {
        // 其他：所有标签类型都可选
        required: [],
        optional: ['FAULT_PHENOMENON', 'CONTROL_UNIT', 'SYSTEM_VOLTAGE', 'VEHICLE_INFO', 'ENGINE_INFO', 'SUBSYSTEM_INFO', 'COMPONENT_INFO']
      }
    };

    // 获取可用的标签类型
    const availableTagTypes = computed(() => {
      if (!props.knowledgeTypeCode) {
        return [];
      }
      const tagMap = KNOWLEDGE_TYPE_TAG_MAP[props.knowledgeTypeCode] || { required: [], optional: [] };
      const allTagCodes = [...tagMap.required, ...tagMap.optional];
      return tagTypes.value.filter(tt => allTagCodes.includes(tt.type_code));
    });

    // 判断是否必选
    const isRequired = (tagTypeCode) => {
      const tagMap = KNOWLEDGE_TYPE_TAG_MAP[props.knowledgeTypeCode] || { required: [] };
      return tagMap.required.includes(tagTypeCode);
    };

    // 判断是否单选（故障现象、系统电压、发动机信息使用单选框）
    const isSingleSelect = (tagTypeCode) => {
      return ['FAULT_PHENOMENON', 'SYSTEM_VOLTAGE', 'ENGINE_INFO'].includes(tagTypeCode);
    };

    // 获取标签值（仅供联动标签 CascadeTagSelector 使用）
    const getTagValue = (tagTypeCode) => {
      const value = selectedTags[tagTypeCode];
      if (!value) {
        return [];
      }
      // 联动标签：selectedTags 存的是 ID 数组，直接返回
      const tagType = tagTypes.value.find(tt => tt.type_code === tagTypeCode);
      if (tagType && tagType.is_cascade === 1) {
        return Array.isArray(value) ? value : [];
      }
      // 独立单选标签：返回第一个值（数字）
      if (isSingleSelect(tagTypeCode)) {
        return Array.isArray(value) && value.length > 0 ? value[0] : undefined;
      }
      return Array.isArray(value) ? value : [];
    };

    // 获取单选框的值（确保类型一致）
    const getSingleSelectValue = (tagTypeCode) => {
      const value = selectedTags[tagTypeCode];
      if (!value || !Array.isArray(value) || value.length === 0) {
        return undefined;
      }
      // 返回数组的第一个值，确保是数字类型（与 tag.id 一致）
      const tagId = value[0];
      if (tagId !== null && tagId !== undefined) {
        return Number(tagId);
      }
      return undefined;
    };

    // 加载独立标签
    const loadIndependentTags = async (tagTypeId) => {
      if (independentTagsMap[tagTypeId]) {
        return; // 已加载
      }
      loadingTags[tagTypeId] = true;
      try {
        const res = await getTagsByType(tagTypeId);
        independentTagsMap[tagTypeId] = res.data || [];
      } catch (error) {
        console.error('加载标签失败:', error);
        independentTagsMap[tagTypeId] = [];
      } finally {
        loadingTags[tagTypeId] = false;
      }
    };

    // 加载所有需要的标签
    const loadAllTags = async () => {
      if (!props.knowledgeTypeCode) {
        console.log('没有知识类型代码，跳过加载标签');
        return;
      }
      
      const tagMap = KNOWLEDGE_TYPE_TAG_MAP[props.knowledgeTypeCode] || { required: [], optional: [] };
      const allTagCodes = [...tagMap.required, ...tagMap.optional];
      console.log('需要加载的标签类型代码:', allTagCodes);
      
      const neededTagTypes = tagTypes.value.filter(tt => allTagCodes.includes(tt.type_code));
      console.log('找到的标签类型:', neededTagTypes.map(tt => ({ id: tt.id, code: tt.type_code, name: tt.type_name })));
      
      for (const tagType of neededTagTypes) {
        if (tagType.is_cascade !== 1) {
          await loadIndependentTags(tagType.id);
        }
      }
      
      console.log('标签加载完成，可用标签类型:', availableTagTypes.value.map(tt => tt.type_name));
    };

    // 单选框点击处理（允许取消选择）
    const handleRadioClick = (tagType, tagId) => {
      const currentValue = getSingleSelectValue(tagType.type_code);
      // 如果点击的是已选中的选项，则取消选择
      if (currentValue === tagId) {
        handleTagChange(tagType, undefined);
      }
    };

    // 清除单选值
    const handleClearSingleSelect = (tagType) => {
      handleTagChange(tagType, undefined);
    };

    // 单选框变化处理
    const handleRadioChange = (tagType, e) => {
      // 处理不同的事件格式：可能是直接的值，也可能是事件对象
      let value;
      console.log('单选框原始事件:', e, '类型:', typeof e, '是否为对象:', typeof e === 'object');
      
      if (e && typeof e === 'object') {
        // 检查是否是事件对象
        if ('target' in e && e.target && 'value' in e.target) {
          value = e.target.value;
        } else if ('value' in e) {
          // 可能是 { value: xxx } 格式
          value = e.value;
        } else if ('nativeEvent' in e) {
          // 可能是合成事件对象，尝试从 target 获取
          const target = e.target || (e.nativeEvent && e.nativeEvent.target);
          if (target && 'value' in target) {
            value = target.value;
          }
        } else {
          // 尝试直接使用对象本身（可能是值对象）
          console.warn('无法从事件对象中提取值，尝试使用对象本身:', e);
          // 如果是对象，尝试查找常见的值属性
          value = e.id || e.tag_id || e.value || e;
        }
      } else {
        // 直接是值
        value = e;
      }
      
      // 确保值是数字类型（与 tag.id 一致）
      if (value !== null && value !== undefined) {
        value = Number(value);
      }
      
      console.log('单选框变化:', { tagTypeCode: tagType.type_code, rawEvent: e, extractedValue: value, valueType: typeof value });
      handleTagChange(tagType, value);
    };

    // 检查标签是否被选中（用于多选标签显示）
    const isTagSelected = (tagTypeCode, tagId) => {
      const tagIds = selectedTags[tagTypeCode];
      return tagIds && Array.isArray(tagIds) && tagIds.includes(tagId);
    };

    // 标签切换（用于多选标签点击）
    const handleTagToggle = (tagType, tagId) => {
      if (!selectedTags[tagType.type_code]) {
        selectedTags[tagType.type_code] = [];
      }
      const tagIds = selectedTags[tagType.type_code];
      const index = tagIds.indexOf(tagId);
      if (index > -1) {
        // 取消选中
        tagIds.splice(index, 1);
      } else {
        // 选中
        tagIds.push(tagId);
      }
      emitChange();
    };

    // 标签变化
    const handleTagChange = (tagType, value) => {
      console.log('标签变化:', { tagTypeCode: tagType.type_code, value, isCascade: tagType.is_cascade === 1, isSingle: isSingleSelect(tagType.type_code) });
      if (tagType.is_cascade === 1) {
        // 联动标签：CascadeTagSelector emit 的已是 ID 数组，直接存储，不再包装
        selectedTags[tagType.type_code] = Array.isArray(value) ? value : (value != null ? [value] : []);
      } else if (isSingleSelect(tagType.type_code)) {
        // 独立单选标签：将单个值包装成数组
        selectedTags[tagType.type_code] = value !== null && value !== undefined ? [value] : [];
      } else {
        // 独立多选标签：直接存储数组
        selectedTags[tagType.type_code] = value || [];
      }
      console.log('更新后的 selectedTags:', selectedTags[tagType.type_code]);
      emitChange();
    };

    // 触发change事件
    const emitChange = () => {
      const tags = [];
      for (const [tagTypeCode, tagIds] of Object.entries(selectedTags)) {
        if (tagIds && tagIds.length > 0) {
          const tagType = tagTypes.value.find(tt => tt.type_code === tagTypeCode);
          if (tagType) {
            // 确保 tagIds 是数组
            const tagIdArray = Array.isArray(tagIds) ? tagIds : [tagIds];
            // 去重
            const uniqueTagIds = [...new Set(tagIdArray)];
            uniqueTagIds.forEach(tagId => {
              tags.push({
                tag_type_id: tagType.id,
                tag_type_code: tagType.type_code,
                tag_category: tagType.is_cascade === 1 ? 'cascade' : 'independent',
                tag_id: tagId,
                tag_value: null // 标签值仅用于展示，查询时使用ID
              });
            });
          }
        }
      }
      emit('change', tags);
    };

    // 加载标签类型
    const loadTagTypes = async () => {
      try {
        const res = await getTagTypes();
        tagTypes.value = res.data || [];
      } catch (error) {
        console.error('加载标签类型失败:', error);
      }
    };

    // 初始化选中值
    const initSelectedTags = () => {
      // 先清空所有选中值
      Object.keys(selectedTags).forEach(key => {
        delete selectedTags[key];
      });
      
      if (props.value && props.value.length > 0) {
        props.value.forEach(tag => {
          if (!selectedTags[tag.tag_type_code]) {
            selectedTags[tag.tag_type_code] = [];
          }
          // 避免重复添加
          if (!selectedTags[tag.tag_type_code].includes(tag.tag_id)) {
            selectedTags[tag.tag_type_code].push(tag.tag_id);
          }
        });
      }
    };

    watch(() => props.value, () => {
      initSelectedTags();
    }, { immediate: true, deep: true });

    watch(() => props.knowledgeTypeCode, async (newCode, oldCode) => {
      console.log('知识类型变化:', { newCode, oldCode, tagTypesLength: tagTypes.value.length });
      
      // 如果没有知识类型代码，不加载标签
      if (!newCode) {
        return;
      }
      
      // 如果知识类型变化，清空选中值
      if (newCode !== oldCode && oldCode) {
        // 清空所有选中值
        Object.keys(selectedTags).forEach(key => {
          delete selectedTags[key];
        });
        // 清空已加载的标签数据
        Object.keys(independentTagsMap).forEach(key => {
          delete independentTagsMap[key];
        });
        // 立即触发change事件清空标签
        emit('change', []);
      }
      
      // 确保标签类型已加载
      if (tagTypes.value.length === 0) {
        await loadTagTypes();
      }
      
      // 加载新类型需要的标签
      await loadAllTags();
      
      // 重新初始化选中值（如果有传入的value）
      initSelectedTags();
    }, { immediate: true });

    watch(() => tagTypes.value, async () => {
      if (tagTypes.value.length > 0 && props.knowledgeTypeCode) {
        await loadAllTags();
      }
    });

    onMounted(async () => {
      await loadTagTypes();
      // 如果已经有知识类型代码，加载对应的标签
      if (props.knowledgeTypeCode) {
        await loadAllTags();
        initSelectedTags();
      }
    });

    return {
      tagTypes,
      availableTagTypes,
      selectedTags,
      independentTagsMap,
      loadingTags,
      isRequired,
      isSingleSelect,
      getTagValue,
      getSingleSelectValue,
      handleRadioChange,
      handleRadioClick,
      handleClearSingleSelect,
      isTagSelected,
      handleTagToggle,
      handleTagChange
    };
  }
};
</script>

<style scoped>
.tag-selector-container {
  background-color: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  margin: 16px 0;
  border: 1px solid #e8e8e8;
}
</style>
